import { Dialog, DialogContent } from 'src/@/components/ui/dialog';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import { Badge } from 'src/@/components/ui/badge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import { mascararTel } from 'src/utils/functions';
import Iconify from 'src/components/Iconify';
import { getSentimentMeta } from 'src/pages/dashboard/AiAnaliseSentimento/AiAnaliseSentimento.design';

function SectionCard({ icon, iconBg, iconColor, title, count, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg} ${iconColor}`}
        >
          <Iconify icon={icon} width={18} />
        </div>
        <h3 className="text-sm font-bold text-gray-900 flex-1">{title}</h3>
        {typeof count === 'number' && count > 0 && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            {count} {count === 1 ? 'item' : 'itens'}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function MetaItem({ icon, label, value, onCopy, copied }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Iconify icon={icon} width={15} className="text-gray-400 flex-shrink-0" />
      <div className="flex flex-col min-w-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
          {label}
        </span>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs text-gray-700 truncate font-mono">{value || '—'}</span>
          {onCopy && value && (
            <button
              type="button"
              onClick={onCopy}
              className="text-gray-400 hover:text-[#7C3AED] transition-colors flex-shrink-0"
              title="Copiar"
            >
              <Iconify
                icon={copied ? 'eva:checkmark-outline' : 'eva:copy-outline'}
                width={13}
                className={copied ? 'text-green-600' : ''}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AiAnalyzeDetailsModal({ open, onClose, data, onOpenChat }) {
  const [copiedChat, setCopiedChat] = useState(false);

  if (!data) return null;

  const meta = getSentimentMeta(data?.result?.sentiment);
  const sentimentValue = parseInt(data?.result?.sentiment, 10);
  const resume = data?.result?.resume;
  const subjects = Array.isArray(data?.result?.subject) ? data.result.subject.filter(Boolean) : [];
  const alerts = Array.isArray(data?.result?.alerts) ? data.result.alerts.filter(Boolean) : [];
  const suggestions = Array.isArray(data?.result?.suggestions)
    ? data.result.suggestions.filter(Boolean)
    : [];

  const handleCopyChat = async () => {
    if (!data?.chat_id) return;
    try {
      await navigator.clipboard.writeText(data.chat_id);
      setCopiedChat(true);
      setTimeout(() => setCopiedChat(false), 2000);
    } catch (err) {}
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 overflow-hidden rounded-2xl gap-0 [&>button.absolute]:hidden">
        {/* Header */}
        <div className="px-6 py-5 pr-16 border-b border-gray-100 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 z-10"
            aria-label="Fechar"
          >
            <Iconify icon="eva:close-outline" width={18} />
          </button>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
              <Iconify icon="mdi:brain" width={22} className="text-[#7C3AED]" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900">Detalhes da Análise</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {data.date
                  ? format(new Date(data.date), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
                      locale: ptBR,
                    })
                  : '—'}
              </p>
            </div>
          </div>

          <Badge
            variant="outline"
            className={`${meta.badge} text-xs font-semibold flex items-center gap-1.5 border px-3 py-1.5 rounded-full flex-shrink-0`}
          >
            <Iconify icon={meta.icon} width={14} />
            {Number.isFinite(sentimentValue) ? `${sentimentValue} · ${meta.label}` : 'Sentimento N/A'}
          </Badge>
        </div>

        {/* Sub-header de metadados */}
        <div className="px-6 py-3 bg-gray-50/60 border-b border-gray-100 grid grid-cols-3 gap-4">
          <MetaItem
            icon="eva:phone-outline"
            label="Telefone"
            value={mascararTel(data.chat_phone)}
          />
          <MetaItem
            icon="mdi:identifier"
            label="Chat ID"
            value={data.chat_id}
            onCopy={handleCopyChat}
            copied={copiedChat}
          />
          <MetaItem
            icon="eva:person-outline"
            label="Atendente"
            value={data.nomeUsuario}
          />
        </div>

        {/* Conteúdo */}
        <ScrollArea className="max-h-[calc(90vh-220px)]">
          <div className="px-6 py-5 space-y-4 bg-gradient-to-b from-slate-50/30 to-white">
            {resume && (
              <SectionCard
                icon="eva:file-text-outline"
                iconBg="bg-violet-50"
                iconColor="text-[#7C3AED]"
                title="Resumo"
              >
                <p className="text-sm text-gray-700 leading-relaxed">{resume}</p>
              </SectionCard>
            )}

            {subjects.length > 0 && (
              <SectionCard
                icon="eva:hash-outline"
                iconBg="bg-blue-50"
                iconColor="text-blue-600"
                title="Assuntos"
                count={subjects.length}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {subjects.map((item, index) => (
                    <div
                      key={index}
                      className="bg-blue-50/70 border border-blue-100 text-blue-800 rounded-lg px-3 py-2 text-xs leading-relaxed"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {alerts.length > 0 && (
              <SectionCard
                icon="eva:alert-triangle-outline"
                iconBg="bg-amber-50"
                iconColor="text-amber-600"
                title="Alertas"
                count={alerts.length}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {alerts.map((alert, index) => (
                    <div
                      key={index}
                      className="bg-amber-50/60 border-l-2 border-amber-400 rounded-r-lg px-3 py-2 text-xs text-amber-900 leading-relaxed"
                    >
                      {alert}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {suggestions.length > 0 && (
              <SectionCard
                icon="eva:bulb-outline"
                iconBg="bg-green-50"
                iconColor="text-green-600"
                title="Sugestões"
                count={suggestions.length}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="bg-green-50/60 border-l-2 border-green-400 rounded-r-lg px-3 py-2 text-xs text-green-900 leading-relaxed"
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {!resume && subjects.length === 0 && alerts.length === 0 && suggestions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
                  <Iconify icon="mdi:information-outline" width={26} className="text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-900">Nenhum dado disponível</p>
                <p className="text-xs text-gray-500 mt-1">
                  Esta análise não retornou conteúdo detalhado.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-gray-100 bg-white flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div className="w-6 h-6 rounded-md bg-violet-50 flex items-center justify-center">
              <Iconify icon="mdi:robot-outline" width={14} className="text-[#7C3AED]" />
            </div>
            <span>Análise gerada automaticamente pela IA</span>
          </div>
          {onOpenChat && data?.chat_id && (
            <button
              type="button"
              onClick={() => onOpenChat(data.chat_id)}
              className="group flex items-center gap-2 pl-4 pr-3 h-9 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/30 hover:shadow-md hover:shadow-[#7C3AED]/30 active:scale-95"
            >
              <Iconify icon="eva:message-circle-outline" width={16} />
              <span>Abrir conversa</span>
              <Iconify
                icon="eva:arrow-ios-forward-outline"
                width={16}
                className="opacity-70 group-hover:translate-x-0.5 transition-transform"
              />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
